// Raporturi nefinalizate — the register of stock reports saved as a draft and
// not yet confirmed (caiet UC50-13). Owner decision D4 of
// docs/decizii-paritate-wireframe-2026-09-05.md: the complete screen, as drawn.
//
// Reference: wireframes/react/src/pages/Nefinalizate.tsx (read-only, AGENTS.md R6).
// Head, lede, five columns, rows that open the editor, ten per page with a
// counter, and the two filters that appear only when there is something to
// choose between.
//
// A draft belongs to its location (CF-03, docs/shape-ops-draft-location-ownership.md):
// GET /stock/drafts answers every draft of the Registrar for its Administrator
// and the drafts of an Operator's locations, whoever started them. The author
// facet appears on its own once a second author is in the list, exactly as the
// wireframe specifies.
//
// ONE THING THE APPLICATION CANNOT SHOW, AND WHY IT IS NOT INVENTED:
//
// The author's name, for a draft started before spec 054. A draft now records
// who started it, at creation (`operatorName`, research R7), and "Creat de"
// shows that. An older draft carries only `operatorIdnp`: for it a name is shown
// for the one person whose name this session does know, itself, and the
// identifier for anyone else rather than a fabricated name.
//
// Every row opens ITS draft, at `/:role/reporting/drafts/{id}` (spec 054 T065,
// FR-024), titled "Finalizează raport". It used to open `/reporting/new` from
// every row, which loaded whichever draft the session had, not the one clicked.
import { useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudIcon, MudSelect, MudTable, MudTag } from '@egov-moldova/mud-react';
import {
  FilterBar,
  PageHeader,
  RouterLink,
  formatDate,
  formatTime,
  type ActiveFilter,
  onRowOpen,
} from '@simsm/ui-shared';
import { listDrafts, listReportingLocations } from '../../../features/reporting/api';
import { fetchSites } from '../../registrar-admin/sites-api';
import { useSession } from '../../../hooks/use-session';
import { QueryState } from '../../../components/QueryState/QueryState';
import { TableFooter, usePageSize } from '../../../components/TableFooter/TableFooter';
import {
  applyDraftFilters,
  buildSiteNames,
  clampPage,
  facetValues,
  hasUsefulFilters,
  isSameDay,
  NO_FILTER,
  pageSlice,
  siteCellLabel,
  toDraftViews,
  type DraftRowView,
} from './drafts-view';

// mud-table's own column type is not re-exported from the package root.
interface TableColumn {
  key: string;
  label: string;
  align?: 'start' | 'center' | 'end';
  width?: string;
}

/** Today, as a calendar date, so "Astăzi" is a day and not a clock reading. */
function todayIso(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function DraftsPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { role: roleSegment } = useParams<{ role: string }>();
  const rolePrefix = `/${roleSegment ?? 'operator'}`;
  // The draft's own address (T064): the report loads it by its id.
  const editorPath = (draftId: string): string => `${rolePrefix}/reporting/drafts/${draftId}`;

  const session = useSession().data;

  const draftsQuery = useQuery({
    queryKey: ['drafts-register'],
    queryFn: listDrafts,
    staleTime: 15_000,
  });

  // A draft names its location by id; the column names it by name. This screen
  // used to read the CONFIRMED-report facets for that, which cannot answer for a
  // location whose first report is still a draft — which is every row here — and
  // so it printed the raw uuid (report A-26).
  //
  // GET /stock/locations already answers it. It exists because caiet UC50 step 3
  // needs the operator's own locations and /admin/sites refuses an Operator
  // (apps/bff/src/stock/locations.controller.ts), it is scoped to the session by
  // construction, and it lists every active location whether or not one ever
  // filed a report. Nothing new goes on the wire for this column.
  //
  // The site registry stays for the one case /stock/locations does not cover: it
  // answers for `registrarScope[0]` only, so an Administrator whose scope spans
  // more than one Registrar would lose the others' locations. An Operator is
  // refused it, so it is asked for only when it will be answered.
  const isRegistrarAdmin = session?.activeRole === 'REGISTRAR_ADMIN';
  const hasRegistrarScope = (session?.registrarScope?.length ?? 0) > 0;

  const reportingLocationsQuery = useQuery({
    queryKey: ['reporting-locations'],
    queryFn: listReportingLocations,
    enabled: hasRegistrarScope,
    staleTime: 5 * 60_000,
  });

  const registrySitesQuery = useQuery({
    queryKey: ['admin-sites', ''],
    queryFn: () => fetchSites(''),
    enabled: isRegistrarAdmin,
    staleTime: 5 * 60_000,
  });

  // A draft bound to a location that was blocked since keeps its name, with the state
  // beside it (BUG-1-088): the active list alone would leave it unnamed.
  const inactiveSiteLabel = t('drafts.site.inactive');
  const siteNames = useMemo(
    () =>
      buildSiteNames(
        {
          active: reportingLocationsQuery.data?.locations ?? [],
          inactive: reportingLocationsQuery.data?.inactiveLocations ?? [],
          registry: registrySitesQuery.data ?? [],
        },
        inactiveSiteLabel,
      ),
    [reportingLocationsQuery.data, registrySitesQuery.data, inactiveSiteLabel],
  );

  const ownName = [session?.firstName, session?.lastName].filter(Boolean).join(' ');

  const all = useMemo(
    () =>
      toDraftViews(
        draftsQuery.data?.drafts ?? [],
        (siteId) => siteNames.get(siteId) ?? '',
        (idnp) => (idnp && idnp === session?.idnp && ownName ? ownName : ''),
      ),
    [draftsQuery.data, siteNames, session?.idnp, ownName],
  );

  const siteFilter = searchParams.get('site') ?? NO_FILTER;
  const authorFilter = searchParams.get('author') ?? NO_FILTER;
  const rows = useMemo(
    () => applyDraftFilters(all, { siteId: siteFilter, authorId: authorFilter }),
    [all, siteFilter, authorFilter],
  );

  const [pageSize, setPageSize] = usePageSize('drafts');
  const page = clampPage(
    Number.parseInt(searchParams.get('p') ?? '1', 10) || 1,
    rows.length,
    pageSize,
  );
  const visible = pageSlice(rows, page, pageSize);

  // The criteria live in the address, so a filtered register can be linked to and
  // reloaded (ACCS-11/12/13). Changing a criterion returns to page one: the page
  // the reader was on describes a list that no longer exists.
  function setCriterion(key: string, value: string): void {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('p');
    setSearchParams(next, { replace: true });
  }

  function goToPage(nextPage: number): void {
    const next = new URLSearchParams(searchParams);
    if (nextPage > 1) next.set('p', String(nextPage));
    else next.delete('p');
    setSearchParams(next, { replace: true });
  }

  function resetCriteria(): void {
    const next = new URLSearchParams(searchParams);
    next.delete('site');
    next.delete('author');
    next.delete('p');
    setSearchParams(next, { replace: true });
  }

  const siteNameOr = (name: string, siteId: string): string =>
    siteCellLabel(name, siteId, {
      unresolved: t('drafts.site.unresolved'),
      unknown: t('drafts.site.unknown'),
    });

  // The label a facet is chosen by. The same fallback chain the cell uses, so a
  // chip and the rows it is filtering never name the same location differently.
  const labelForSite = (siteId: string): string => siteNameOr(siteNames.get(siteId) ?? '', siteId);
  const labelForAuthor = (idnp: string): string =>
    all.find((row) => row.authorId === idnp)?.authorLabel || idnp;

  const siteOptions = facetValues(all, (row) => row.siteId);
  const authorOptions = facetValues(all, (row) => row.authorId);
  const showFilters = hasUsefulFilters(all);

  const activeFilters: ActiveFilter[] = [
    ...(siteFilter
      ? [
          {
            key: 'site',
            label: t('drafts.column.site'),
            value: labelForSite(siteFilter),
            onRemove: () => setCriterion('site', NO_FILTER),
          },
        ]
      : []),
    ...(authorFilter
      ? [
          {
            key: 'author',
            label: t('drafts.created_by'),
            value: labelForAuthor(authorFilter),
            onRemove: () => setCriterion('author', NO_FILTER),
          },
        ]
      : []),
  ];

  const columns: TableColumn[] = [
    { key: 'site', label: t('drafts.column.site') },
    { key: 'created', label: t('drafts.column.created') },
    { key: 'rows', label: t('drafts.column.rows'), align: 'end' },
    { key: 'errors', label: t('drafts.column.errors'), align: 'end' },
    { key: 'status', label: t('drafts.column.status') },
  ];

  const today = todayIso();
  function createdLabel(row: DraftRowView): string {
    const day = isSameDay(row.createdAtIso, today)
      ? t('drafts.created.today')
      : formatDate(row.createdAtIso);
    return `${day}, ${formatTime(row.createdAtIso)}`;
  }
  function siteLabel(row: DraftRowView): string {
    return siteNameOr(row.siteLabel, row.siteId);
  }
  function authorLabel(row: DraftRowView): string {
    return row.authorLabel || row.authorId || t('drafts.author.unknown');
  }

  const tableRows = visible.map((row) => ({
    id: row.id,
    site: siteLabel(row),
    created: createdLabel(row),
    rows: String(row.rowCount),
    errors: String(row.errorCount),
    status: t('drafts.status.draft'),
  }));

  return (
    <section className="simsm-drafts">
      <PageHeader title={t('drafts.title')} lede={t('drafts.lede')} />

      {/* The one filter pattern in the system (owner ruling of 2026-08-23,
          decision D2), and the wireframe's own rule for this screen: the bar is
          offered only when a facet has more than one value, so an operator alone
          at one location is not given two controls that can only return the list
          already on screen. No search box: the wireframe gives this register
          none, and five columns of dates and counts are not searched by text. */}
      {showFilters ? (
        <FilterBar
          active={activeFilters}
          onReset={resetCriteria}
          labels={{
            filters: t('common.filter.button'),
            reset: t('common.filter.reset'),
            panelAriaLabel: t('drafts.filter.aria_label'),
            activeAriaLabel: t('common.filter.active_aria_label'),
          }}
          panel={
            <>
              {siteOptions.length > 1 ? (
                <label className="simsm-filter-bar__field">
                  <span className="simsm-filter-bar__field-label">{t('drafts.column.site')}</span>
                  <MudSelect
                    size="medium"
                    value={siteFilter}
                    aria-label={t('drafts.column.site')}
                    placeholder={t('drafts.filter.all_sites')}
                    onMudChange={(e: CustomEvent<{ value: string }>) =>
                      setCriterion('site', e.detail.value)
                    }
                  >
                    <option value={NO_FILTER}>{t('drafts.filter.all_sites')}</option>
                    {siteOptions.map((siteId) => (
                      <option key={siteId} value={siteId}>
                        {labelForSite(siteId)}
                      </option>
                    ))}
                  </MudSelect>
                </label>
              ) : null}
              {authorOptions.length > 1 ? (
                <label className="simsm-filter-bar__field">
                  <span className="simsm-filter-bar__field-label">{t('drafts.created_by')}</span>
                  <MudSelect
                    size="medium"
                    value={authorFilter}
                    aria-label={t('drafts.created_by')}
                    placeholder={t('drafts.filter.any_author')}
                    onMudChange={(e: CustomEvent<{ value: string }>) =>
                      setCriterion('author', e.detail.value)
                    }
                  >
                    <option value={NO_FILTER}>{t('drafts.filter.any_author')}</option>
                    {authorOptions.map((idnp) => (
                      <option key={idnp} value={idnp}>
                        {labelForAuthor(idnp)}
                      </option>
                    ))}
                  </MudSelect>
                </label>
              ) : null}
            </>
          }
        />
      ) : null}

      <QueryState
        query={draftsQuery}
        isEmpty={all.length === 0}
        empty={
          // The wireframe's `.empty` for this screen: a glyph and one line, no
          // call to action, because an empty drafts register is the good state.
          // Decision D3 makes the empty state a shared component; when that lands
          // this block is the first thing it replaces.
          <div className="simsm-drafts__empty">
            {/* The wireframe draws this glyph at 48px. AGE's icon scale stops at
                24 (`mud-icon` accepts 12/16/20/24 and nothing else), so 24 is
                the largest rung available and the block keeps the wireframe's
                generous padding around it rather than a forked icon size.
                AGE-GAP(icon-scale) · vendor/age-design/SOURCE_COMMIT */}
            <MudIcon name="checkmark-large" size={24} />
            <p className="simsm-drafts__empty-title">{t('drafts.empty.title')}</p>
          </div>
        }
      >
        <div className="simsm-drafts__card">
          <MudTable
            className="simsm-table"
            columns={columns}
            rows={tableRows}
            hoverable={true}
            // Divided rows, not zebra. The wireframe has a `zebra` modifier and
            // uses it on the reports register and the site register; on this one
            // it does not (Nefinalizate.tsx `tbl compact lista` against
            // Rapoarte.tsx `tbl zebra compact`). A deliberate difference in the
            // drawing, so it is a deliberate difference here.
            rowStyle="divided"
            disableSort={true}
            aria-label={t('drafts.table.aria_label')}
            onMudRowClick={onRowOpen((index) => {
              const draft = visible[index];
              if (draft) navigate(editorPath(draft.id));
            })}
          >
            {/* Filtered down to nothing is not the same as having no drafts, so
                the message says how many the filters are hiding, in the table's
                own empty slot: the card, the headers and the footer stay
                standing, the way the wireframe leaves them. */}
            <span slot="empty" className="simsm-drafts__filtered-out">
              {all.length === 1
                ? t('drafts.empty_filtered_one')
                : t('drafts.empty_filtered', { count: all.length })}
            </span>
            {columns.map((column) => (
              <span
                key={`header-${column.key}`}
                slot={`header-cell-${column.key}`}
                className="simsm-table__th"
              >
                {column.label}
              </span>
            ))}
            {visible.map((row, index) => [
              // The row click alone would leave the register reachable with the
              // mouse only: mud-table gives its body rows no tabindex and no
              // focusable descendant, so Tab would skip every draft (the defect
              // the submissions register fixed the same way, WCAG SC 2.1.1). The
              // location is AGE's link on the editor's route, which is
              // focusable, announced, and leaves the row click intact.
              <RouterLink
                key={`site-${row.id}`}
                slot={`cell-site-${index}`}
                to={editorPath(row.id)}
                size="sm"
                // Ink, not institutional blue. The wireframe draws this cell as
                // plain text in a row that is itself clickable, and AGE has the
                // treatment as a prop: `strict` is its high-emphasis link colour
                // for dense copy. The hover underline and the focus ring stay,
                // so the column still reads and behaves as a link.
                variant="strict"
                underline="hover"
                aria-label={t('drafts.row.open', {
                  site: siteLabel(row),
                  created: createdLabel(row),
                })}
              >
                {siteLabel(row)}
              </RouterLink>,
              // Creat is two lines in the wireframe: when, over who by. The date
              // stays whole; the name below it may wrap.
              <span
                key={`created-${row.id}`}
                slot={`cell-created-${index}`}
                className="simsm-drafts__created"
              >
                <span className="simsm-table__keep">{createdLabel(row)}</span>
                <span className="simsm-drafts__meta">
                  <span className="simsm-drafts__meta-key">{t('drafts.created_by')}:</span>{' '}
                  {authorLabel(row)}
                </span>
              </span>,
              <span
                key={`rows-${row.id}`}
                slot={`cell-rows-${index}`}
                className="simsm-drafts__num"
              >
                {row.rowCount}
              </span>,
              // A zero is a plain number; anything above it is a danger tag, so
              // the drafts that need work are findable by colour down the column.
              <span
                key={`errors-${row.id}`}
                slot={`cell-errors-${index}`}
                className="simsm-drafts__num"
              >
                {row.errorCount > 0 ? (
                  <MudTag
                    variant="status"
                    size="sm"
                    semantic="danger"
                    label={String(row.errorCount)}
                  />
                ) : (
                  '0'
                )}
              </span>,
              <span key={`status-${row.id}`} slot={`cell-status-${index}`}>
                {/* The wireframe's class is `.tag.accent`, but its two tokens
                    are the brand pair (`--bg-brand-sec` on `--text-brand`,
                    mud.css:654) — tinted blue on blue. AGE spells that
                    `semantic="brand"`; AGE's own `accent` is the amber rung.
                    Matching the drawing means following its colours, not its
                    class name. */}
                <MudTag
                  variant="status"
                  size="sm"
                  semantic="brand"
                  label={t('drafts.status.draft')}
                />
              </span>,
            ])}
          </MudTable>

          <TableFooter
            total={rows.length}
            page={page}
            pageSize={pageSize}
            onPage={goToPage}
            onPageSize={setPageSize}
          />
        </div>
      </QueryState>
    </section>
  );
}
